import { Appointment, HealthAssessment, MedicalCertificate, SystemConfig } from '../types';
import { formatUkDate } from './dates';

const FALLBACK_LOGO_URL = 'https://placehold.co/150x50/009FE3/ffffff?text=K2+OH';
const FALLBACK_SIGNATURE_URL = 'https://placehold.co/150x50/white/000?text=Signed';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isHttpLikeUrl = (value: string) => /^(https?:|data:image\/)/i.test(value);

// Certificates are legal medical documents rendered from an admin-editable
// template, then shown to the patient in a print window / PDF capture. The
// template must never yield an interactive document: no scripts, no
// contenteditable regions, no live form controls (a patient could otherwise
// alter their outcome before printing). Applied to every render regardless of
// what the stored template contains.
const stripInteractiveTagAttributes = (tag: string) =>
  tag
    .replace(/\scontenteditable\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/\scontenteditable(?=[\s/>])/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');

export const sanitizeCertificateHtml = (html: string) =>
  String(html || '')
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<script\b[^>]*>/gi, '')
    .replace(/<[a-zA-Z][^>]*>/g, stripInteractiveTagAttributes)
    .replace(/<(input|textarea|select|button)\b/gi, '<$1 disabled');

const sanitizeImageUrl = (value: unknown, fallback: string) => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  if (!isHttpLikeUrl(raw)) return fallback;
  return raw;
};

export const formatCertificateDate = (value?: string | null, fallback = 'N/A') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  return formatUkDate(raw, raw || fallback);
};

export const buildCertificateVerificationUrl = ({
  systemConfig,
  certificateId
}: {
  systemConfig: SystemConfig;
  certificateId: string;
}) => {
  const baseUrl = (
    systemConfig.verification?.urlOverride ||
    systemConfig.general.appUrl ||
    window.location.origin
  ).replace(/\/$/, '');

  return `${baseUrl}?mode=verify&certId=${encodeURIComponent(certificateId)}`;
};

const replaceImagePlaceholder = ({
  html,
  token,
  imageUrl,
  alt,
  style
}: {
  html: string;
  token: string;
  imageUrl: string;
  alt: string;
  style: string;
}) => {
  const escapedUrl = escapeHtml(imageUrl);
  const srcPlaceholderPattern = new RegExp(`src=(["'])\\{${token}\\}\\1`, 'g');
  const standalonePattern = new RegExp(`\\{${token}\\}`, 'g');

  return html
    .replace(srcPlaceholderPattern, `src="${escapedUrl}"`)
    .replace(
      standalonePattern,
      `<img src="${escapedUrl}" alt="${escapeHtml(alt)}" style="${escapeHtml(style)}" />`
    );
};

// The certificate stores restrictions as a single scalar string, but the technician
// selects them as predefined clauses (a string[]) in the Health Assessment when they
// mark the patient "Fit with Restrictions". Derive the certificate's restrictions text
// from those clauses so the selection actually reaches the generated certificate, and
// append any additional free-text restrictions the technician typed on the certificate
// panel. When no clauses are selected, the free text is used on its own.
export const resolveCertificateRestrictions = (
  healthAssessment: HealthAssessment | undefined,
  freeTextRestrictions?: string | null
): string | undefined => {
  const clauses =
    healthAssessment?.fitStatus === 'fit_restrictions'
      ? (healthAssessment.restrictions || []).map((r) => String(r || '').trim()).filter(Boolean)
      : [];

  const freeText = String(freeTextRestrictions ?? '').trim();
  const parts = [...clauses, ...(freeText ? [freeText] : [])];

  return parts.length > 0 ? parts.join('; ') : undefined;
};

export const buildCertificateHtml = ({
  template,
  systemConfig,
  appointment,
  certificate,
  clientName
}: {
  template: string;
  systemConfig: SystemConfig;
  appointment: Appointment;
  certificate: MedicalCertificate;
  clientName: string;
}) => {
  const verificationUrl = buildCertificateVerificationUrl({
    systemConfig,
    certificateId: certificate.id
  });
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(verificationUrl)}`;

  const logoUrl = sanitizeImageUrl(systemConfig.general.logoUrl, FALLBACK_LOGO_URL);
  const signatureUrl = sanitizeImageUrl(certificate.technicianSignature, FALLBACK_SIGNATURE_URL);
  // Prefer the job title captured on the certificate (confirmed/edited by the
  // technician at issue time); fall back to the appointment snapshot for
  // certificates issued before per-certificate job titles existed.
  const jobTitle =
    String(certificate.jobTitle || '').trim() || String(appointment.customerJobTitle || '').trim();

  let html = String(template || '');
  html = replaceImagePlaceholder({
    html,
    token: 'app_logo',
    imageUrl: logoUrl,
    alt: 'App logo',
    style: 'max-height:56px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'signature',
    imageUrl: signatureUrl,
    alt: 'Technician signature',
    style: 'max-height:60px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'qr_code',
    imageUrl: qrCodeUrl,
    alt: 'Verification QR',
    style: 'width:100px;height:100px;'
  });

  // Per-service/test rows. Names are snapshotted on the certificate at issue
  // time — never re-joined against the live services/add-ons catalogs.
  const OUTCOME_COLUMNS = ['fit', 'fit_restrictions', 'referred', 'unfit'] as const;
  const DA_OUTCOME_LABEL: Record<string, string> = {
    negative: 'NEGATIVE',
    non_negative: 'NON-NEGATIVE',
    not_tested: 'NOT TESTED'
  };
  const certificateResults = Array.isArray(certificate.results) ? certificate.results : [];
  const resultsRowsHtml = certificateResults
    .map((row) => {
      const isDrugOutcome = row.outcome in DA_OUTCOME_LABEL;
      const cells = isDrugOutcome
        ? `<td colspan="4" style="text-align:center">${escapeHtml(DA_OUTCOME_LABEL[row.outcome])}</td>`
        : OUTCOME_COLUMNS.map(
            (column) =>
              `<td style="text-align:center">${row.outcome === column ? '&#10003;' : ''}</td>`
          ).join('');
      return `<tr><td>${escapeHtml(row.name)}</td>${cells}</tr>`;
    })
    .join('');
  const resultServiceNames = certificateResults
    .filter((row) => row.kind === 'service')
    .map((row) => row.name);
  const serviceNamesText =
    resultServiceNames.length > 0
      ? resultServiceNames.join(', ')
      : String(appointment.serviceName || '').trim();
  const assessedForText = certificateResults
    .filter((row) => row.kind === 'add_on')
    .map((row) => row.name)
    .join(', ');

  const replacements: Record<string, string> = {
    service_names: escapeHtml(serviceNamesText),
    results_rows: resultsRowsHtml,
    assessed_for: escapeHtml(assessedForText),
    name: escapeHtml(appointment.customerName || 'N/A'),
    dob: escapeHtml(formatCertificateDate(appointment.customerDob, 'N/A')),
    job_title: escapeHtml(jobTitle || 'N/A'),
    client_name: escapeHtml(clientName || 'N/A'),
    date_of_medical: escapeHtml(formatCertificateDate(certificate.issuedAt)),
    expiry_date: escapeHtml(formatCertificateDate(certificate.validUntil)),
    technician_name: escapeHtml(certificate.technicianName || 'N/A'),
    outcome: escapeHtml(
      String(certificate.outcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    drug_outcome: escapeHtml(
      String(certificate.drugTestOutcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    advice: escapeHtml(certificate.advice || 'None'),
    restrictions: certificate.restrictions
      ? `<p><strong>Restrictions:</strong> ${escapeHtml(certificate.restrictions)}</p>`
      : '',
    // Prefer snapshotted test names from the results; raw ids only for legacy
    // certificates issued before per-test results existed.
    addons_list: escapeHtml(assessedForText || (appointment.addOnIds || []).join(', ') || 'None'),
    ref: escapeHtml(certificate.id || '')
  };

  Object.entries(replacements).forEach(([token, replacement]) => {
    html = html.replace(new RegExp(`\\{${token}\\}`, 'g'), replacement);
  });

  return sanitizeCertificateHtml(html);
};

const toFileSafeSegment = (value: string, fallback: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

const formatFileDate = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return 'date-unknown';
  const prefixMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (prefixMatch) return `${prefixMatch[1]}-${prefixMatch[2]}-${prefixMatch[3]}`;
  return toFileSafeSegment(formatCertificateDate(raw, 'date-unknown'), 'date-unknown');
};

export const resolveCertificateClientName = (
  appointment: Appointment,
  companyNameFallback?: string
) => {
  const appointmentCompanyName = String(appointment.companyName || '').trim();
  if (appointmentCompanyName) return appointmentCompanyName;

  const fallback = String(companyNameFallback || '').trim();
  if (fallback) return fallback;

  if (appointment.companyId) return 'Corporate Client';
  return 'Individual';
};

export const buildCertificateFileName = ({
  appointment,
  certificate
}: {
  appointment: Appointment;
  certificate: MedicalCertificate;
}) => {
  const idPart = toFileSafeSegment(certificate.id, 'certificate');
  const namePart = toFileSafeSegment(appointment.customerName || 'patient', 'patient');
  const datePart = formatFileDate(certificate.issuedAt || appointment.date);
  return `certificate-${idPart}-${namePart}-${datePart}`;
};

// Browsers omit CSS background images/colors when printing unless the page
// opts into exact color adjustment ("Background graphics" is unticked by
// default in the print dialog). The certificate template draws the outcome and
// drug-test ticks as background images, so without this a certificate that
// looks correct on screen prints — or saves to PDF from the print dialog —
// with the ticks silently missing.
const PRINT_BACKGROUNDS_STYLE =
  '<style>* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }</style>';

export const forcePrintedBackgrounds = (html: string): string => {
  const headClose = /<\/head\s*>/i.exec(html);
  if (headClose) {
    return html.slice(0, headClose.index) + PRINT_BACKGROUNDS_STYLE + html.slice(headClose.index);
  }
  return PRINT_BACKGROUNDS_STYLE + html;
};

export const openBlankPrintWindow = (): Window | null =>
  window.open('', '', 'width=800,height=900');

export const writeCertificatePrintWindow = (
  html: string,
  suggestedFileName?: string,
  existingWindow?: Window | null
) => {
  if (!html) {
    existingWindow?.close();
    return false;
  }
  const printWindow = existingWindow ?? window.open('', '', 'width=800,height=900');
  if (!printWindow) return false;

  printWindow.document.write(forcePrintedBackgrounds(html));
  printWindow.document.close();
  const title = String(suggestedFileName || 'certificate').trim() || 'certificate';
  printWindow.document.title = title;
  setTimeout(() => printWindow.print(), 500);
  return true;
};

export const openCertificatePrintWindow = (html: string, suggestedFileName?: string) =>
  writeCertificatePrintWindow(html, suggestedFileName);

export const canPrintCertificate = (certificate?: MedicalCertificate | null): boolean => {
  if (!certificate) return false;
  return (certificate.status ?? 'active') === 'active';
};
